import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ThecData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { getMultiGoalStatus, getThresholdGoalState } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function SatisfactionCard({
  data,
  kind,
}: {
  data: ThecData;
  kind: 'employer' | 'participant';
}) {
  const theme = useMantineTheme();
  const series = kind === 'employer' ? data.employerSatisfaction : data.participantSatisfaction;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const goal = 80;
  const allTimeAvg = kind === 'employer' ? data.employerSatisfactionAllTimeAvg : data.participantSatisfactionAllTimeAvg;

  const parseQuarterLabel = (label: string): { year: number; quarter: 1 | 2 | 3 | 4 } | null => {
    const m1 = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (m1) return { year: Number(m1[2]), quarter: Number(m1[1]) as 1 | 2 | 3 | 4 };
    const m2 = label.match(/^(\d{4})\s+Q([1-4])$/);
    if (m2) return { year: Number(m2[1]), quarter: Number(m2[2]) as 1 | 2 | 3 | 4 };
    return null;
  };

  const curMeta = parseQuarterLabel(cur?.quarter ?? '');
  const curYear = curMeta?.year ?? null;
  const curQuarterNum = curMeta?.quarter ?? null;

  const ytdAvg =
    curYear === null || curQuarterNum === null
      ? null
      : (() => {
          const ytdPoints = series.filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          });
          if (ytdPoints.length === 0) return null;
          return ytdPoints.reduce((acc, p) => acc + p.percentSatisfied, 0) / ytdPoints.length;
        })();

  const prevYearYtdAvg =
    curYear === null || curQuarterNum === null
      ? null
      : (() => {
          const ytdPoints = series.filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          });
          if (ytdPoints.length === 0) return null;
          return ytdPoints.reduce((acc, p) => acc + p.percentSatisfied, 0) / ytdPoints.length;
        })();

  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqPct = cur ? pctChange(cur.percentSatisfied, prev?.percentSatisfied ?? null) : null;
  const qoqText = qoqPct === null ? '—' : arrow(qoqPct) ? `${arrow(qoqPct)}${Math.abs(qoqPct).toFixed(2)}% QoQ` : '0.00% QoQ';

  const yoyPct = ytdAvg === null || prevYearYtdAvg === null ? null : pctChange(ytdAvg, prevYearYtdAvg);
  const yoyText = yoyPct === null ? '—' : arrow(yoyPct) ? `${arrow(yoyPct)}${Math.abs(yoyPct).toFixed(2)}% YoY` : '0.00% YoY';

  const hasYtdGoal = false;
  const currentDisplay = Math.round(cur?.percentSatisfied ?? 0);
  const ytdDisplay = ytdAvg === null ? null : Math.round(ytdAvg);
  const allTimeDisplay = allTimeAvg === null ? null : Math.round(allTimeAvg);
  const status = getMultiGoalStatus([
    getThresholdGoalState({ value: currentDisplay, goal, direction: 'min' }),
    ...(hasYtdGoal && ytdDisplay !== null
      ? [getThresholdGoalState({ value: ytdDisplay, goal, direction: 'min' })]
      : []),
    ...(allTimeDisplay !== null ? [getThresholdGoalState({ value: allTimeDisplay, goal, direction: 'min' })] : []),
  ]);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const title = kind === 'employer' ? 'Employer Satisfaction' : 'Participant Satisfaction';
  const seriesTotal = series.reduce((sum, point) => sum + point.percentSatisfied, 0);
  const buildAllTimeData = (priorCount: number) => {
    const priorTotal = allTimeAvg * (series.length + priorCount) - seriesTotal;
    return { priorCount, priorTotal };
  };
  let { priorCount, priorTotal } = buildAllTimeData(4);
  if (priorTotal < 0 || priorTotal > 100 * priorCount) {
    ({ priorCount, priorTotal } = buildAllTimeData(1));
  }
  if (priorTotal < 0 || priorTotal > 100 * priorCount) {
    priorCount = 0;
    priorTotal = 0;
  }
  let runningTotal = priorTotal;
  const allTimeData = series.map((point, idx) => {
    runningTotal += point.percentSatisfied;
    const divisor = priorCount + idx + 1;
    return {
      quarter: point.quarter,
      percentSatisfied: point.percentSatisfied,
      avgAllTime: divisor > 0 ? runningTotal / divisor : point.percentSatisfied,
    };
  });

  return (
    <CardShell
      title={title}
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Percent satisfied for the current quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur ? `${Math.round(cur.percentSatisfied)}%` : '—'}
                </Text>
              ),
              sublabel: (
                <Stack spacing={0}>
                  <Text size="xs" color="dimmed">
                    {qoqText}
                  </Text>
                  <Text size="xs" color="dimmed">
                    Goal {goal}%
                  </Text>
                </Stack>
              ),
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date average satisfaction (across quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytdAvg === null ? '—' : `${Math.round(ytdAvg)}%`}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'All-Time',
              labelTooltip: `All-time average satisfaction (includes periods not shown). Goal: ${goal}%.`,
              value: (
                <Text size="lg" weight={800}>
                  {Math.round(allTimeAvg)}%
                </Text>
              ),
              sublabel: `Goal ${goal}%`,
            },
          ]}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '1rem',
            alignItems: 'start',
            marginTop: '0.75rem',
            '@media (max-width: 980px)': {
              gridTemplateColumns: '1fr',
            },
          }}
        >
          <Box>
            <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginBottom: '0.5rem' }}>
              Quarterly Satisfaction Trend
            </Text>
            <Box sx={{ height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
                  <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                  <YAxis
                    domain={[0, 100]}
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    tickMargin={8}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <ReferenceLine
                    y={goal}
                    stroke="#fa5252"
                    strokeDasharray="4 4"
                    ifOverflow="extendDomain"
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const v = payload[0]?.value;
                      return (
                        <div style={tooltipShellStyle as any}>
                          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                          <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                            {typeof v === 'number' ? Math.round(v) : '—'}%
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Bar
                    dataKey="percentSatisfied"
                    name="Satisfaction"
                    fill={theme.colors.blue[6]}
                    radius={[6, 6, 0, 0]}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <Box>
            <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginBottom: '0.5rem' }}>
              All-Time Satisfaction Average
            </Text>
            <Box sx={{ height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={allTimeData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
                  <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                  <YAxis
                    domain={[0, 100]}
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    tickMargin={8}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <ReferenceLine y={goal} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const p: any = payload[0]?.payload;
                      if (!p) return null;
                      return (
                        <div style={tooltipShellStyle as any}>
                          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                          <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                            <div style={{ fontWeight: 900, fontSize: 14 }}>{Math.round(p.avgAllTime)}% All-Time Avg</div>
                            <div style={{ opacity: 0.9 }}>{Math.round(p.percentSatisfied)}% this quarter</div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="avgAllTime"
                    name="All-Time Average"
                    stroke={theme.colors.blue[6]}
                    strokeWidth={2}
                    dot={(dotProps: any) => {
                      const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                      return (
                        <circle
                          key={`thec-satisfaction-dot-${pointKey}`}
                          cx={dotProps.cx}
                          cy={dotProps.cy}
                          r={2}
                          fill={theme.colors.blue[6]}
                          stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                          strokeWidth={1.5}
                        />
                      );
                    }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      </Stack>
    </CardShell>
  );
}
